import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { Separator } from '../../components/ui/separator';
import { 
  Truck, MapPin, Package, Clock, Plus, Trash, PencilSimple,
  CircleNotch, GlobeHemisphereWest, CalendarBlank, Timer,
  Warning, CheckCircle, Info, ArrowsClockwise
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WEEKDAYS = [
  { value: 'lundi', label: 'Lundi' },
  { value: 'mardi', label: 'Mardi' },
  { value: 'mercredi', label: 'Mercredi' },
  { value: 'jeudi', label: 'Jeudi' },
  { value: 'vendredi', label: 'Vendredi' },
  { value: 'samedi', label: 'Samedi' },
];

const CARRIERS = [
  { value: 'local_delivery', label: 'Livraison locale' },
  { value: 'colissimo', label: 'Colissimo' },
  { value: 'chronopost', label: 'Chronopost' },
  { value: 'chronofresh', label: 'Chronofresh' },
  { value: 'mondial_relay', label: 'Mondial Relay' },
  { value: 'pickup', label: 'Retrait sur place' },
  { value: 'custom', label: 'Personnalise' },
];

const EMPTY_FORM = {
  name: '', carrier: 'local_delivery', rule_type: 'delivery', price: 0,
  free_above: null, min_order: null, max_order: null,
  postal_codes: '', countries: ['FR'],
  cities: '', delivery_note: '',
  delivery_days: [], time_slots: '',
  estimated_days: '', is_active: true,
  weight_rates: [],
};

const ShippingPage = () => {
  const { currentStore } = useStore();

  if (!currentStore) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Selectionnez une boutique pour configurer l'expedition</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shipping-page">
      <div>
        <h1 className="font-cabinet text-3xl font-bold tracking-tight">Règles de livraison</h1>
        <p className="text-slate-500 mt-1">Configurez vos zones de livraison, jours disponibles et delais de preparation</p>
      </div>

      <PreparationConfig />
      <Separator />
      <ShippingRulesSection />
    </div>
  );
};

// ════════════════════════════════════════
// PREPARATION TIME CONFIG
// ════════════════════════════════════════
const PreparationConfig = () => {
  const { currentStore } = useStore();
  const [config, setConfig] = useState({ preparation_days: 1, cutoff_hour: 18, pickup_same_day_cutoff_hour: 16, pickup_min_prep_hours: 0, message: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentStore) return;
    axios.get(`${API_URL}/api/stores/${currentStore.id}/preparation-config`, { withCredentials: true })
      .then(r => setConfig(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentStore]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/stores/${currentStore.id}/preparation-config`, config, { withCredentials: true });
      setConfig(res.data.config);
      toast.success('Delai de preparation mis a jour');
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-6 text-center"><CircleNotch className="animate-spin mx-auto" size={24} /></div>;

  return (
    <Card className="dashboard-card" data-testid="preparation-config">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <Timer size={22} className="text-amber-600" weight="fill" />
          </div>
          <div>
            <CardTitle>Delai de preparation</CardTitle>
            <CardDescription>Temps necessaire pour preparer une commande avant expedition/livraison</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Jours de preparation</Label>
            <Select value={String(config.preparation_days)} onValueChange={v => setConfig(p => ({ ...p, preparation_days: parseInt(v) }))}>
              <SelectTrigger data-testid="prep-days-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 7].map(d => (
                  <SelectItem key={d} value={String(d)}>{d} jour{d > 1 ? 's' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">Nombre de jours ouvres pour preparer la commande</p>
          </div>
          <div className="space-y-2">
            <Label>Heure limite de commande</Label>
            <Select value={String(config.cutoff_hour)} onValueChange={v => setConfig(p => ({ ...p, cutoff_hour: parseInt(v) }))}>
              <SelectTrigger data-testid="cutoff-hour-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map(h => (
                  <SelectItem key={h} value={String(h)}>{h}h00</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">Commande apres cette heure = +1 jour</p>
          </div>
          <div className="space-y-2">
            <Label>Message client</Label>
            <Input
              value={config.message}
              onChange={e => setConfig(p => ({ ...p, message: e.target.value }))}
              placeholder="Votre commande sera preparee sous {days} jour(s)..."
              data-testid="prep-message"
            />
            <p className="text-xs text-slate-500">Utilisez {'{days}'} pour le nombre de jours</p>
          </div>
        </div>
        <div className="border-t border-slate-100 pt-4">
          <p className="text-sm font-semibold text-slate-700 mb-1">Retrait en boutique — jour même</p>
          <p className="text-xs text-slate-500 mb-3">Le retrait le jour même ne s'applique qu'au retrait en boutique. La livraison garde le délai de préparation ci-dessus.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Heure limite pour retrait le jour même</Label>
              <Select value={String(config.pickup_same_day_cutoff_hour ?? 16)} onValueChange={v => setConfig(p => ({ ...p, pickup_same_day_cutoff_hour: parseInt(v) }))}>
                <SelectTrigger data-testid="pickup-cutoff-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19].map(h => (
                    <SelectItem key={h} value={String(h)}>{h}h00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Commande après cette heure = retrait à partir du lendemain</p>
            </div>
            <div className="space-y-2">
              <Label>Délai minimum de préparation</Label>
              <Select value={String(config.pickup_min_prep_hours ?? 0)} onValueChange={v => setConfig(p => ({ ...p, pickup_min_prep_hours: parseInt(v) }))}>
                <SelectTrigger data-testid="pickup-prep-hours-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[0, 1, 2, 3, 4].map(h => (
                    <SelectItem key={h} value={String(h)}>{h === 0 ? 'Aucun' : `${h} heure${h > 1 ? 's' : ''}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Seuls les créneaux du jour encore réalisables sont proposés</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <Info size={18} className="flex-shrink-0" />
          <span>Avec {config.preparation_days} jour(s) de preparation et une heure limite a {config.cutoff_hour}h, les dates de livraison proposees au client commenceront au plus tot {config.preparation_days + 1} jour(s) apres la commande. Retrait boutique : jour même possible avant {config.pickup_same_day_cutoff_hour ?? 16}h{(config.pickup_min_prep_hours ?? 0) > 0 ? ` (préparation ${config.pickup_min_prep_hours}h min)` : ' (pas de délai minimum)'}.</span>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="save-preparation">
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </CardContent>
    </Card>
  );
};

// ════════════════════════════════════════
// SHIPPING RULES (ZONES)
// ════════════════════════════════════════
const ShippingRulesSection = () => {
  const { currentStore } = useStore();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [weightRateForm, setWeightRateForm] = useState({ min_kg: '', max_kg: '', price: '' });

  const fetchRules = useCallback(async () => {
    if (!currentStore) return;
    try {
      const res = await axios.get(`${API_URL}/api/stores/${currentStore.id}/shipping-rules`, { withCredentials: true });
      setRules(res.data);
    } catch { } finally { setLoading(false); }
  }, [currentStore]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const openNew = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setOpen(true);
  };

  const openEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      name: rule.name || '',
      carrier: rule.carrier || 'local_delivery',
      rule_type: rule.rule_type || 'delivery',
      price: rule.price || 0,
      free_above: rule.free_above || null,
      min_order: rule.min_order || null,
      max_order: rule.max_order || null,
      postal_codes: (rule.postal_codes || []).join(', '),
      countries: rule.countries || ['FR'],
      cities: rule.cities || '',
      delivery_note: rule.delivery_note || '',
      delivery_days: rule.delivery_days || [],
      time_slots: (rule.time_slots || []).join(', '),
      estimated_days: rule.estimated_days || '',
      is_active: rule.is_active !== false,
      weight_rates: rule.weight_rates || [],
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Nom requis'); return; }
    try {
      const payload = {
        ...form,
        postal_codes: form.postal_codes ? form.postal_codes.split(',').map(s => s.trim()).filter(Boolean) : [],
        time_slots: form.time_slots ? form.time_slots.split(',').map(s => s.trim()).filter(Boolean) : [],
        free_above: form.free_above || null,
        min_order: form.min_order || null,
        max_order: form.max_order || null,
      };
      if (editingId) {
        await axios.put(`${API_URL}/api/stores/${currentStore.id}/shipping-rules/${editingId}`, payload, { withCredentials: true });
        toast.success('Regle mise a jour');
      } else {
        await axios.post(`${API_URL}/api/stores/${currentStore.id}/shipping-rules`, payload, { withCredentials: true });
        toast.success('Regle creee');
      }
      setOpen(false);
      fetchRules();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette regle de livraison ?')) return;
    try {
      await axios.delete(`${API_URL}/api/stores/${currentStore.id}/shipping-rules/${id}`, { withCredentials: true });
      toast.success('Supprimee');
      fetchRules();
    } catch { toast.error('Erreur'); }
  };

  const handleRestoreOfficial = async () => {
    if (!window.confirm("Remplacer TOUTES les zones de livraison actuelles par les zones officielles MAGERE ?\n\n• Zone 1 — Mardi, jeudi ou samedi (Istres, Fos-sur-Mer, Martigues...)\n• Zone 2 — Mercredi ou vendredi (Miramas, Salon-de-Provence, Cavaillon...)\n35€ de frais, offert dès 100€, livraison avant 12h.\n\nLes anciennes règles de livraison/expédition seront supprimées (le retrait en boutique est conservé).")) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/stores/${currentStore.id}/shipping-rules/restore-official`, {}, { withCredentials: true });
      toast.success(`Zones officielles installées (${data.deleted} ancienne(s) règle(s) supprimée(s))`);
      fetchRules();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const toggleDay = (day) => {
    setForm(p => ({
      ...p,
      delivery_days: p.delivery_days.includes(day)
        ? p.delivery_days.filter(d => d !== day)
        : [...p.delivery_days, day]
    }));
  };

  const addWeightRate = () => {
    const { min_kg, max_kg, price } = weightRateForm;
    if (!min_kg || !max_kg || !price) { toast.error('Remplissez tous les champs du bareme poids'); return; }
    setForm(p => ({
      ...p,
      weight_rates: [...p.weight_rates, {
        min_kg: parseFloat(min_kg), max_kg: parseFloat(max_kg), price: parseFloat(price)
      }]
    }));
    setWeightRateForm({ min_kg: '', max_kg: '', price: '' });
  };

  const removeWeightRate = (idx) => {
    setForm(p => ({ ...p, weight_rates: p.weight_rates.filter((_, i) => i !== idx) }));
  };

  if (!currentStore) return null;

  return (
    <Card className="dashboard-card" data-testid="shipping-rules-section">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
              <GlobeHemisphereWest size={22} className="text-blue-600" weight="fill" />
            </div>
            <div>
              <CardTitle>Zones de livraison</CardTitle>
              <CardDescription>Definissez vos zones par codes postaux avec jours de livraison et conditions</CardDescription>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleRestoreOfficial} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="restore-official-zones-btn">
              <ArrowsClockwise className="mr-1" size={16} /> Zones officielles
            </Button>
            <Button onClick={openNew} className="bg-[#0052FF]" data-testid="new-rule-btn">
              <Plus className="mr-1" size={16} /> Nouvelle zone
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-8 text-center"><CircleNotch className="animate-spin mx-auto" size={24} /></div>
        ) : rules.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Truck size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="font-medium">Aucune zone de livraison configuree</p>
            <p className="text-sm mt-1">Creez des zones pour definir ou et quand vous livrez</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zone</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Codes postaux</TableHead>
                <TableHead>Jours</TableHead>
                <TableHead>Prix</TableHead>
                <TableHead>Min. panier</TableHead>
                <TableHead>Creneaux</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map(r => (
                <TableRow key={r.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {r.rule_type === 'pickup' ? <MapPin size={16} className="text-emerald-500" /> : <Truck size={16} className="text-blue-500" />}
                      <span className="font-medium">{r.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={r.rule_type === 'pickup' ? 'secondary' : r.rule_type === 'expedition' ? 'outline' : 'default'} className="text-xs capitalize">
                      {r.rule_type === 'pickup' ? 'Retrait' : r.rule_type === 'expedition' ? 'Expedition' : 'Livraison'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs max-w-[140px]">
                    {r.postal_codes?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {r.postal_codes.slice(0, 4).map((pc, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-slate-100 rounded text-[11px]">{pc}</span>
                        ))}
                        {r.postal_codes.length > 4 && <span className="text-slate-400 text-[11px]">+{r.postal_codes.length - 4}</span>}
                      </div>
                    ) : <span className="text-slate-400">Tous</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {(r.delivery_days || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {r.delivery_days.map((d, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[11px] capitalize">{d.slice(0, 3)}</span>
                        ))}
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="font-semibold text-sm">
                    {r.price === 0 ? <span className="text-emerald-600">Gratuit</span> : `${r.price?.toFixed(2)} EUR`}
                    {r.weight_rates?.length > 0 && <span className="text-[10px] text-slate-400 block">+ bareme poids</span>}
                  </TableCell>
                  <TableCell className="text-sm">
                    {r.min_order ? <span className="font-medium">{r.min_order} EUR</span> : <span className="text-slate-400">-</span>}
                  </TableCell>
                  <TableCell className="text-xs">{r.time_slots?.length > 0 ? `${r.time_slots.length} creneau(x)` : '-'}</TableCell>
                  <TableCell>
                    <Badge variant={r.is_active ? 'default' : 'secondary'} className="text-[10px]">{r.is_active ? 'Active' : 'Inactive'}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`edit-rule-${r.id}`}>
                        <PencilSimple size={16} className="text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(r.id)} data-testid={`delete-rule-${r.id}`}>
                        <Trash size={16} className="text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* ── Create / Edit Dialog ── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifier la zone' : 'Nouvelle zone de livraison'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">

            {/* Name + Type */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Nom de la zone *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Bouches-du-Rhone" data-testid="rule-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={form.rule_type} onValueChange={v => setForm(p => ({ ...p, rule_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="delivery">Livraison</SelectItem>
                    <SelectItem value="pickup">Retrait</SelectItem>
                    <SelectItem value="expedition">Expedition (hors zone)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Postal codes */}
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <MapPin size={14} /> Codes postaux (separes par virgule)
              </Label>
              <Input value={form.postal_codes} onChange={e => setForm(p => ({ ...p, postal_codes: e.target.value }))} placeholder="Ex: 13800, 13270, 13110" data-testid="rule-postal-codes" />
              <p className="text-xs text-slate-500">Code complet (13800 = Istres uniquement) ou prefixe court (13 = tout le departement). Vide = partout.</p>
            </div>

            {/* Cities */}
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <GlobeHemisphereWest size={14} /> Villes desservies (separees par virgule)
              </Label>
              <textarea
                value={form.cities}
                onChange={e => setForm(p => ({ ...p, cities: e.target.value }))}
                placeholder="Ex: Istres, Fos-sur-Mer, Martigues"
                rows={3}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="rule-cities"
              />
              <p className="text-xs text-slate-500">La zone s'applique si le code postal OU la ville du client correspond.</p>
            </div>

            {/* Delivery note */}
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Info size={14} /> Note affichee au client (optionnel)
              </Label>
              <Input value={form.delivery_note} onChange={e => setForm(p => ({ ...p, delivery_note: e.target.value }))} placeholder="Ex: Port-Saint-Louis-du-Rhône seulement le jeudi" data-testid="rule-delivery-note" />
            </div>

            {/* Delivery Days - Checkboxes */}
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <CalendarBlank size={14} /> Jours de livraison disponibles
              </Label>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map(d => {
                  const selected = form.delivery_days.includes(d.value);
                  return (
                    <button key={d.value} type="button" onClick={() => toggleDay(d.value)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all ${selected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                      data-testid={`day-${d.value}`}>
                      {d.label}
                    </button>
                  );
                })}
              </div>
              {form.delivery_days.length === 0 && (
                <p className="text-xs text-amber-600 flex items-center gap-1"><Warning size={12} /> Selectionnez au moins un jour</p>
              )}
            </div>

            {/* Price + Min order */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Prix de base (EUR)</Label>
                <Input type="number" step="0.01" value={form.price} onChange={e => setForm(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} data-testid="rule-price" />
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1">Min. panier (EUR) <span className="text-red-500">*</span></Label>
                <Input type="number" step="0.01" value={form.min_order || ''} onChange={e => setForm(p => ({ ...p, min_order: e.target.value ? parseFloat(e.target.value) : null }))} placeholder="Ex: 50" data-testid="rule-min-order" />
                <p className="text-xs text-slate-500">Montant minimum du panier pour cette zone</p>
              </div>
            </div>

            {/* Free above */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Gratuit a partir de (EUR)</Label>
                <Input type="number" step="0.01" value={form.free_above || ''} onChange={e => setForm(p => ({ ...p, free_above: e.target.value ? parseFloat(e.target.value) : null }))} placeholder="Ex: 80" />
              </div>
              <div className="space-y-1.5">
                <Label>Transporteur</Label>
                <Select value={form.carrier} onValueChange={v => setForm(p => ({ ...p, carrier: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CARRIERS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Weight rates (for expedition type) */}
            {form.rule_type === 'expedition' && (
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg border">
                <Label className="flex items-center gap-1.5">
                  <Package size={14} /> Bareme par poids (expedition)
                </Label>
                {form.weight_rates.length > 0 && (
                  <div className="space-y-1">
                    {form.weight_rates.map((wr, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className="bg-white px-2 py-1 rounded border text-xs">{wr.min_kg}kg - {wr.max_kg}kg</span>
                        <span className="font-semibold">{wr.price.toFixed(2)} EUR</span>
                        <button onClick={() => removeWeightRate(idx)} className="text-red-400 hover:text-red-600"><Trash size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-4 gap-2">
                  <Input type="number" step="0.1" placeholder="Min kg" value={weightRateForm.min_kg} onChange={e => setWeightRateForm(p => ({ ...p, min_kg: e.target.value }))} />
                  <Input type="number" step="0.1" placeholder="Max kg" value={weightRateForm.max_kg} onChange={e => setWeightRateForm(p => ({ ...p, max_kg: e.target.value }))} />
                  <Input type="number" step="0.01" placeholder="Prix EUR" value={weightRateForm.price} onChange={e => setWeightRateForm(p => ({ ...p, price: e.target.value }))} />
                  <Button variant="outline" size="sm" onClick={addWeightRate}>Ajouter</Button>
                </div>
              </div>
            )}

            {/* Time slots */}
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Clock size={14} /> Creneaux horaires (separes par virgule)
              </Label>
              <Input value={form.time_slots} onChange={e => setForm(p => ({ ...p, time_slots: e.target.value }))} placeholder="Ex: 09h-12h, 14h-17h" data-testid="rule-time-slots" />
            </div>

            {/* Active + Estimated days */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Delai estime (affichage)</Label>
                <Input value={form.estimated_days} onChange={e => setForm(p => ({ ...p, estimated_days: e.target.value }))} placeholder="2-4 jours" />
              </div>
              <div className="flex items-end gap-2 pb-1">
                <Switch checked={form.is_active} onCheckedChange={v => setForm(p => ({ ...p, is_active: v }))} />
                <Label>Active</Label>
              </div>
            </div>

            <Button onClick={handleSave} className="w-full bg-[#0052FF]" data-testid="save-rule-btn">
              {editingId ? 'Mettre a jour' : 'Creer la zone'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ShippingPage;
